import type { IssuePriority, IssueSize } from './types';
import { PRIORITY_LABELS, PRIORITY_ORDER, SIZE_LABEL, SIZE_ORDER } from './types';
import { parseWhen, dueSuggestions } from './when';

/**
 * ── PARSING A QUICK-ADD LINE — WS-3 ─────────────────────────────────────────
 *
 * One line makes a card: a title, then tokens that set real fields —
 *
 *     *type    @assignee    #phase    !priority    ~size    ^due
 *
 * This is the pure half, apart from React and the store so it can be tested on
 * its own: it takes the line and what the board HAS (its types, people, phases)
 * and answers what each token resolved to. A token that matches nothing is a
 * MISS, kept and reported rather than folded into the title or dropped in
 * silence — a typo should read as "not applied", never quietly change the card.
 *
 * The same world drives `quickAddSuggest` below, which lists what an unfinished
 * token could become so the bar can offer them to click — Vinnie, 10 Sep 2026:
 * *"I wish I could click on … @ and then a name, I don't just see one name I see
 * all the names that start with those letters and be able to click it."* So a
 * key you have half-forgotten shows its choices rather than needing to be known.
 *
 * `^` sets the card's TARGET DATE (Track B), understood as an ISO day or a small
 * plain-word vocabulary — see src/when.ts. An unrecognised date is a miss like
 * any other, never a guessed day. Discipline is still not a field, so it has no
 * token: a token for a column the save cannot write is a promise the card cannot
 * keep.
 */

/** The words that name a priority, so a person types the rung they think in —
 *  "p1", "critical" and "crit" are one answer. */
export const PRIORITY_TOKENS: Record<string, IssuePriority> = {
  p1: 'critical', crit: 'critical', critical: 'critical',
  p2: 'high', hi: 'high', high: 'high',
  p3: 'medium', med: 'medium', medium: 'medium', normal: 'medium',
  p4: 'low', lo: 'low', low: 'low',
};

/** The words that name a size, the same idea: type the one you think in. */
export const SIZE_TOKENS: Record<string, IssueSize> = {
  q: 'quick', quick: 'quick', s: 'quick', small: 'quick',
  m: 'medium', med: 'medium', medium: 'medium',
  l: 'long', long: 'long', lg: 'long', large: 'long',
};

export interface QuickAddWorld {
  types: { slug: string; name: string }[];
  people: { email: string; name: string }[];
  phases: { id: string; name: string }[];
}

export interface QuickAddResult {
  title: string;
  kind: { slug: string; name: string } | null;
  assignee: { email: string; name: string } | null;
  phase: { id: string; name: string } | null;
  priority: IssuePriority | null;
  size: IssueSize | null;
  /** A `YYYY-MM-DD` target day, or null — set by the `^` token (see src/when.ts). */
  due: string | null;
  misses: string[];
}

/** True when `value` begins the whole word or any word within `name`, ignoring
 *  case and the spaces between words — so `#build` finds "Build & configure" and
 *  `@marcia` finds "Marcia Alleyne". */
function nameMatches(name: string, value: string): boolean {
  const low = name.toLowerCase();
  if (low.replace(/\s+/g, '').startsWith(value)) return true;
  return low.split(/\s+/).some((w) => w.startsWith(value));
}

/** Lowercase and drop the separators people put in handles, so a typed value
 *  and a stored one can be compared without either side's dots getting in the
 *  way — `marcia.alleyne` and `marciaalleyne` are the same handle. */
const norm = (s: string): string => s.toLowerCase().replace(/[._\-\s]/g, '');

/** The bit of an assignee people actually type: the local part of an email, or
 *  the id of an assistant (stored as `assistant:<id>`). What the menu inserts
 *  after `@`, and what the parser resolves back — so a click always round-trips
 *  to exactly the one that was clicked (email local parts and ids are unique). */
export function assigneeHandle(email: string): string {
  return email.includes('@') ? email.split('@')[0] : email.replace(/^assistant:/, '');
}

/** Options for the parser. `noPhase` turns off the `#` token entirely, so `#`
 *  is ordinary text — for the Plan's add-card box, where the phase is fixed to
 *  the phase you are adding into (so no #phase is needed), and where `#132` must
 *  stay a way to find a card by its ref rather than being read as a phase. */
export interface QuickAddOptions {
  noPhase?: boolean;
}

export function parseQuickAdd(raw: string, world: QuickAddWorld, opts: QuickAddOptions = {}): QuickAddResult {
  const out: QuickAddResult = { title: '', kind: null, assignee: null, phase: null, priority: null, size: null, due: null, misses: [] };
  const title: string[] = [];

  for (const tok of raw.trim().split(/\s+/)) {
    if (!tok) continue;
    const head = tok[0];
    const val = tok.slice(1).toLowerCase();

    if (head === '*' && val) {
      const t = world.types.find((x) => x.slug.toLowerCase().startsWith(val) || x.name.toLowerCase().startsWith(val));
      if (t) out.kind = { slug: t.slug, name: t.name };
      else out.misses.push(tok);
    } else if (head === '@' && val) {
      const p = world.people.find(
        (x) => norm(assigneeHandle(x.email)).startsWith(norm(val)) || nameMatches(x.name, val)
      );
      if (p) out.assignee = p;
      else out.misses.push(tok);
    } else if (head === '#' && val && !opts.noPhase) {
      const ph = world.phases.find((x) => nameMatches(x.name, val));
      if (ph) out.phase = { id: ph.id, name: ph.name };
      else out.misses.push(tok);
    } else if (head === '!' && val) {
      const pr = PRIORITY_TOKENS[val];
      if (pr) out.priority = pr;
      else out.misses.push(tok);
    } else if (head === '~' && val) {
      const sz = SIZE_TOKENS[val];
      if (sz) out.size = sz;
      else out.misses.push(tok);
    } else if (head === '^' && val) {
      const day = parseWhen(val);
      if (day) out.due = day;
      else out.misses.push(tok);
    } else {
      title.push(tok);
    }
  }

  out.title = title.join(' ');
  return out;
}

/* ── WHAT AN UNFINISHED TOKEN COULD BECOME — the click-to-pick menu ─────────── */

/** The five tokens that carry a menu, and the word for each, so the menu can
 *  name the field you are half-way through — "Assignee", not "@". */
export const QUICK_ADD_FIELDS: Record<string, string> = {
  '*': 'Type', '@': 'Assignee', '#': 'Phase', '!': 'Priority', '~': 'Size', '^': 'Due',
};

export interface QuickAddSuggestion {
  /** What to put after the sigil when this is chosen — resolves back to itself. */
  token: string;
  /** What the person reads: the type name, the person's name, the rung. */
  label: string;
  /** A quieter second line — an email, or "Assistant" — to tell two apart. */
  sub?: string;
}

/**
 * The values a token could resolve to, for the menu under the bar. An empty
 * `partial` (a bare sigil) offers everything the field has, so typing just `@`
 * shows every name; as more is typed it narrows the same way the parser matches,
 * so whatever is offered will resolve to itself when inserted. Capped, because a
 * menu longer than the screen is its own kind of unhelpful.
 */
export function quickAddSuggest(sigil: string, partial: string, world: QuickAddWorld): QuickAddSuggestion[] {
  const v = partial.toLowerCase();
  const nv = norm(partial);
  const hit = (token: string, name: string) => !partial || norm(token).startsWith(nv) || nameMatches(name, v);

  let all: QuickAddSuggestion[];
  switch (sigil) {
    case '*':
      all = world.types.map((t) => ({ token: t.slug, label: t.name }));
      break;
    case '@': {
      /* Names only — no email addresses. Vinnie, 10 Sep 2026: *"assignee email
         addresses don't need to be in the popup … just the names of people and
         assistants."* An assistant keeps a quiet "Assistant" tag (a category,
         not an address) so the two kinds still tell apart. */
      const toSug = (p: { email: string; name: string }): QuickAddSuggestion => ({
        token: assigneeHandle(p.email),
        label: p.name,
        sub: p.email.startsWith('assistant:') ? 'Assistant' : undefined,
      });
      const matched = world.people.map(toSug).filter((c) => hit(c.token, c.label));
      /*
       * EVERYONE WHO CAN HOLD A CARD IS OFFERED — this menu is NOT capped, so it
       * shows exactly who the card's Assignee dropdown shows and the two can
       * never disagree about who a card may go to (Vinnie, 11 Sep). It used to
       * slice to eight, which on a board with eight-plus assignable people
       * dropped whoever fell past the eighth — first the assistants, then, once
       * those were spared, a person — so the @ menu and the card disagreed. The
       * popover scrolls (.qadd-ac has max-height), so a long list stays bounded
       * on screen, and typing a letter or two narrows it the usual way. People
       * come first, assistants after, because a card is usually handed to one.
       */
      const bots = matched.filter((c) => c.sub === 'Assistant');
      const persons = matched.filter((c) => c.sub !== 'Assistant');
      return [...persons, ...bots];
    }
    case '#':
      /* First word of the name — unique enough on a board whose phase names are
         already kept distinct, and what reads back cleanly in the line. */
      all = world.phases.map((p) => ({ token: p.name.split(/\s+/)[0].toLowerCase(), label: p.name }));
      break;
    case '!':
      all = PRIORITY_ORDER.map((p) => ({ token: p, label: PRIORITY_LABELS[p] }));
      break;
    case '~':
      all = SIZE_ORDER.map((s) => ({ token: s, label: SIZE_LABEL[s] }));
      break;
    case '^':
      /* The date options, each a word parseWhen understands, with the day it
         lands on as the quiet second line. Returned directly (not through the
         slice below) because the subs are already computed. */
      return dueSuggestions().filter((c) => hit(c.token, c.label));
    default:
      return [];
  }
  return all.filter((c) => hit(c.token, c.label)).slice(0, 8);
}
